<template>
  <div class="report-summary mb-3">
    <!-- Photo service header -->
    <div class="justify-content-between d-flex primary-header">
      <div class="d-flex align-items-center">
        <h6 class="font-weight-semi-bold my-0 mr-2">{{ photoService.company_name }}</h6>
        <div v-if="isIndustryER">({{ photoService.totals.order_count | number }})</div>
      </div>
      <div class="d-flex align-items-center">
        <h6 class="dot dot-blue font-weight-semi-bold my-0">
          {{ photoService.totals.total_amount_eur | currency(currencyCodes.EUR) }}
        </h6>
        <h6 class="dot dot-green font-weight-semi-bold my-0 ml-2">
          {{ photoService.totals.turnover | currency(currencyCodes.EUR) }}
        </h6>
        <h6 class="dot dot-yellow font-weight-semi-bold my-0 ml-2">
          {{ photoService.totals.presale | currency(currencyCodes.EUR) }}
        </h6>
        
        <div>
          <CButton :class="['btn-grid-toggle', {'on': photoService._toggled && !loadingEvents, 'loading': loadingEvents}]"
                   :disabled="loadingEvents"
                   @click="toggleDetails">
            <div v-if="loadingEvents" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </CButton>
        </div>
      </div>
    </div>
    
    <!-- Secondary header per photo service -->
    <div class="d-flex flex-column flex-md-row secondary-header justify-content-between">
      <template v-if="isIndustryER">
        <div class="mx-1">
          <translate>Avg purchase</translate>
          <span class="font-weight-semi-bold mx-2">
            {{ getAverage(photoService.totals.total_amount_eur, photoService.totals.order_count) | currency(currencyCodes.EUR) }}
          </span>
        </div>
        <div class="mx-1">
          <translate>Remarketing</translate>
          ({{ getPercentage(photoService.totals.total_amount_eur, photoService.totals.order_from_remarketing_amount_eur) | number }}%)
          <span class="font-weight-semi-bold mx-2">
            {{ photoService.totals.order_from_remarketing_amount_eur | currency(currencyCodes.EUR) }}
          </span>
        </div>
        <div class="mx-1" v-if="industry === industriesKeys.RESORT">
          <translate>During</translate>
          ({{ getPercentage(photoService.totals.total_amount_eur, photoService.totals.order_during_amount_eur) | number }}%)
          <span class="font-weight-semi-bold mx-2">
            {{ photoService.totals.order_during_amount_eur | currency(currencyCodes.EUR) }}
          </span>
        </div>
        <div class="mx-1" v-if="industry === industriesKeys.RESORT">
          <translate>After</translate>
          ({{ getPercentage(photoService.totals.total_amount_eur, (photoService.totals.total_amount_eur - photoService.totals.order_during_amount_eur)) | number }}%)
          <span class="font-weight-semi-bold mx-2">
            {{ (photoService.totals.total_amount_eur - photoService.totals.order_during_amount_eur) | currency(currencyCodes.EUR) }}
          </span>
        </div>
      </template>
      <template v-else>
        <div class="mx-1">
          <translate>Check-in</translate>
          <span class="font-weight-semi-bold mx-2">{{ photoService.totals.checkin_count | number }}</span>
        </div>
        <div class="mx-1">
          <translate>Remarketing</translate>
          ({{ getPercentage(photoService.totals.total_amount_eur, photoService.totals.order_from_remarketing_amount_eur) | number }}%)
          <span class="font-weight-semi-bold mx-2">
            {{ photoService.totals.order_from_remarketing_amount_eur | currency(currencyCodes.EUR) }}
          </span>
        </div>
        <div class="mx-1">
          <translate>Avg photos per guest</translate>
          <span class="font-weight-semi-bold mx-2">
            {{ (photoService.totals.order_medias_count / photoService.totals.checkin_count || 0) | number }}
          </span>
        </div>
        <div class="mx-1">
          <translate>Avg downloads</translate>
          <span class="font-weight-semi-bold mx-2">
            {{ (photoService.totals.download_count / photoService.totals.checkin_count || 0) | number }}
          </span>
        </div>
        <div class="mx-1">
          <translate>Avg shares</translate>
          <span class="font-weight-semi-bold mx-2">
            {{ (((photoService.totals.share_total_count - photoService.totals.download_count) / photoService.totals.checkin_count)) || 0 | number }}%
          </span>
        </div>
      </template>
    </div>
    
    <!-- Collapsed section for events -->
    <CCollapse :show="Boolean(photoService._toggled)" :duration="300">
      <div style="min-height: 100px">
        <PhotoServiceEventsTable 
          :photo-service="photoService"
          :industry="industry"
          @events-loaded="$emit('events-loaded', $event)"
        />
      </div>
    </CCollapse>
  </div>
</template>

<script>
import { currencyCodes } from "@/constants"
import { 
  INDUSTRIES_ER, 
  industriesKeys 
} from "@/views_pica/stats/constants"
import { getAverage, getPercentage } from "@/utils/functions"
import PhotoServiceEventsTable from "./PhotoServiceEventsTable.vue"

export default {
  name: "PhotoServiceItem",
  
  components: {
    PhotoServiceEventsTable
  },
  
  props: {
    photoService: {
      type: Object,
      required: true
    },
    industry: {
      type: String,
      required: true
    },
    loadingEvents: {
      type: Boolean,
      default: false
    }
  },
  
  computed: {
    currencyCodes() {
      return currencyCodes
    },
    
    industriesKeys() {
      return industriesKeys
    },
    
    isIndustryER() {
      return INDUSTRIES_ER.includes(this.industry)
    }
  },
  
  methods: {
    getPercentage,
    getAverage,
    toggleDetails() {
      this.$emit('toggle-details', this.photoService)
    }
  }
}
</script>

<style scoped>
.btn-grid-toggle.loading::before {
  display: none;
}
</style>